import { useTranslation } from 'react-i18next'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { formatHhMm, type FuelPlan } from '@jupi/shared'
import { RemoteContent, Title } from '@jupi/ui'
import FlightRulesToggle from './FlightRulesToggle'
import { usePlanDraft } from './usePlanDraft'

// The fuel plan for the plan's route and aircraft: trip fuel, contingency,
// reserve, what is required against what is usable, and the endurance.
// The final reserve (VFR 30 min, IFR 45 min) is set here and on the plan page, next to the aircraft.
export default function FuelPage() {
  const { t } = useTranslation()
  const editor = usePlanDraft()
  const { figures, draft } = editor

  return (
    <Box>
      <Title label={t('nav.planFuel')} />
      <RemoteContent isLoading={editor.isLoading} error={editor.error}>
        {figures ? (
          draft?.registration ? (
            <Box sx={{ maxWidth: 560 }}>
              <FlightRulesToggle reserveMinutes={editor.reserveMinutes} onReserve={editor.setReserveMinutes} sx={{ mb: 2 }} />
              <FuelCard fuel={figures.fuel} reserveMinutes={editor.reserveMinutes} />
            </Box>
          ) : (
            <Alert severity="info">{t('plan.editor.noAircraft')}</Alert>
          )
        ) : null}
      </RemoteContent>
    </Box>
  )
}

function line(label: string, value: string, strong = false) {
  return (
    <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
      <Typography variant="body2" color={strong ? 'text.primary' : 'text.secondary'} sx={{ fontWeight: strong ? 700 : 400 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: strong ? 700 : 400 }}>
        {value}
      </Typography>
    </Stack>
  )
}

function FuelCard({
  fuel,
  reserveMinutes,
}: {
  fuel: FuelPlan | null
  reserveMinutes: number
}) {
  const { t } = useTranslation()
  return (
    <Card>
      <CardContent>
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">{t('plan.weight.fuelPlan')}</Typography>
          {fuel ? <Chip size="small" color={fuel.sufficient ? 'success' : 'error'} label={fuel.sufficient ? 'OK' : t('plan.editor.fuelShort')} /> : null}
        </Stack>
        {fuel ? (
          <Stack spacing={0.75}>
            {line(t('plan.editor.fuelTrip'), `${fuel.tripUsg.toFixed(1)} USG`)}
            {line(t('plan.weight.contingency'), `${fuel.contingencyUsg.toFixed(1)} USG`)}
            {line(`${t('plan.weight.reserve')} (${reserveMinutes} min)`, `${fuel.reserveUsg.toFixed(1)} USG`)}
            {line(t('plan.weight.required'), `${fuel.requiredUsg.toFixed(1)} USG`, true)}
            {line(t('plan.weight.usable'), `${fuel.usableUsg.toFixed(1)} USG`, true)}
            {line(t('plan.weight.margin'), `${fuel.marginUsg.toFixed(1)} USG`)}
            {line(t('plan.weight.onArrival'), `${fuel.onArrivalUsg.toFixed(1)} USG`)}
            {line(t('plan.weight.endurance'), formatHhMm(fuel.enduranceMinutes))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('plan.weight.noBurn')}
          </Typography>
        )}
      </CardContent>
    </Card>
  )
}
